import { FieldValues, Path, PathValue, useWatch } from 'react-hook-form'
import { useEffect } from 'react'
import { ControlledDropDown } from '@ui-kit'
import { CategoryPropTypes } from '../../types/common'
import { useAppDispatch, useAppSelector } from '../../utils/agent.store'
import { getCategoryDropdownData } from '@app-store'
import { isEqual } from 'lodash'

const CategoryInput = <T, U extends FieldValues>({
  categoryKey,
  setValue,
  searchLength,
  behaviorCategoryFilter,
  ...rest
}: CategoryPropTypes<T, U>) => {
  const dispatch = useAppDispatch()
  const fieldValue = useWatch({
    control: rest.control,
    name: rest.name as Path<U>,
  })

  const categoryOptions =
    useAppSelector(
      (state) => state?.categoryDropdown?.categoryMap?.[categoryKey]
    ) ?? []

  const options = !behaviorCategoryFilter
    ? categoryOptions
    : categoryOptions.filter((op) => {
        const selectedId = Number(fieldValue?.id)
        if (!Number.isNaN(selectedId) && Number(op.id) === selectedId) {
          return true
        }
        if (
          behaviorCategoryFilter.allowedIds !== undefined &&
          !behaviorCategoryFilter.allowedIds.includes(Number(op.id))
        ) {
          return false
        }
        if (behaviorCategoryFilter.allowedLabels !== undefined) {
          const label = String(op.value ?? '').trim()
          if (!behaviorCategoryFilter.allowedLabels.includes(label)) {
            return false
          }
        }
        return true
      })

  const updateValue = () => {
    if (!fieldValue?.id && fieldValue?.id !== 0) {
      const newVal = fieldValue === '' ? '' : fieldValue?.id
      if (isEqual(fieldValue, newVal)) {
        setValue(
          `lookupLabels.${rest.name}` as Path<U>,
          newVal as PathValue<U, Path<U>>
        )
        return
      }

      setValue?.(rest.name, newVal as PathValue<U, Path<U>>)
      setValue(
        `lookupLabels.${rest.name}` as Path<U>,
        newVal as PathValue<U, Path<U>>
      )
      return
    }

    const formattedValue = { id: fieldValue?.id }

    if (!isEqual(fieldValue, formattedValue)) {
      setValue?.(rest.name, formattedValue as PathValue<U, Path<U>>)
    }
    const label = options.find((op) => op.id === formattedValue?.id)?.value
    setValue(
      `lookupLabels.${rest.name}` as Path<U>,
      label as PathValue<U, Path<U>>
    )
  }

  useEffect(() => {
    dispatch(getCategoryDropdownData(categoryKey))
  }, [])

  useEffect(() => {
    updateValue()
  }, [fieldValue, options])

  return (
    <ControlledDropDown<T, U>
      {...rest}
      isCrossable
      options={options.map((val) => ({
        value: { id: val.id } as unknown as string,
        label: val.value,
      }))}
      searchLength={searchLength}
      key={fieldValue?.id}
    />
  )
}

export default CategoryInput
